export const MOBILE_QUALITY_VALUES = ['high', 'normal', 'low'] as const;

export type MobileQuality = (typeof MOBILE_QUALITY_VALUES)[number];

export interface MobileQualityRuntimeOptions {
  splatBudgetMillions: number;
  webgl2TouchSplatBudget?: number;
  dprScale: number;
  lodRangeMin: number;
  lodRangeMax: number;
  minPixelSize: number;
  minContribution: number;
}

export function parseMobileQuality(value: unknown): MobileQuality {
  return MOBILE_QUALITY_VALUES.includes(value as MobileQuality) ? (value as MobileQuality) : 'normal';
}

export function mobileQualityToRuntimeOptions(quality: MobileQuality): MobileQualityRuntimeOptions {
  switch (quality) {
    case 'high':
      return {
        splatBudgetMillions: 1.5,
        webgl2TouchSplatBudget: 1_500_000,
        dprScale: 1,
        lodRangeMin: 0,
        lodRangeMax: 99,
        minPixelSize: 1,
        minContribution: 0,
      };
    case 'normal':
      return {
        splatBudgetMillions: 0.85,
        webgl2TouchSplatBudget: 850_000,
        dprScale: 0.85,
        lodRangeMin: 1,
        lodRangeMax: 99,
        minPixelSize: 1.15,
        minContribution: 0.00015,
      };
    case 'low':
    default:
      return {
        splatBudgetMillions: 0.45,
        dprScale: 0.75,
        lodRangeMin: 3,
        lodRangeMax: 99,
        minPixelSize: 1.6,
        minContribution: 0.0006,
      };
  }
}

export function mobileQualityToEntranceRuntimeOptions(
  quality: MobileQuality,
): MobileQualityRuntimeOptions {
  const safeQuality = quality === 'high' ? 'normal' : quality;
  return {
    ...mobileQualityToRuntimeOptions(safeQuality),
    // resolveBootstrapLodRange replaces this range with the asset's coarsest available LOD.
    // Finer nearby LODs are enabled only after worldReady.
    lodRangeMin: 99,
    lodRangeMax: 99,
  };
}

export function mobileQualityLabel(quality: MobileQuality): string {
  switch (quality) {
    case 'high':
      return 'High quality';
    case 'low':
      return 'Low';
    case 'normal':
    default:
      return 'Normal';
  }
}
